import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import { HttpClient, HttpStatusCode } from '@angular/common/http';
import { BehaviorSubject, map } from 'rxjs';
import { ProfileModel } from '@shared/models/profile.model';
import { LoginRequest } from '@shared/models/login.request';
import { CommonService } from './common.service';
import { v4 as uuidv4 } from 'uuid';
import { commonConstant } from '@auth/common.constant';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends HttpService {
  private $accountLogin: BehaviorSubject<ProfileModel | null>;
  private keyAccessToken = 'accessToken';
  private keyRefreshToken = 'refreshToken';

  constructor(http: HttpClient, private commonService: CommonService) {
    super(http);
    this.$accountLogin = new BehaviorSubject<ProfileModel | null>(null);
  }

  login(request: LoginRequest) {
    return super.postAsync('login', request);
  }

  logout() {
    // Clear all modals when manually logout
    this.commonService.clearAllModals();
    return super.getAsync('logout');
  }

  async getProfileAPI() {
    const response = await super.getAsync('profile');
    if (response.statusCode === HttpStatusCode.Ok) {
      this.$accountLogin.next(response.data);
    } else {
      this.$accountLogin.next(null);
      if (response.statusCode !== HttpStatusCode.Unauthorized) {
        const timeout = setTimeout(() => {
          const errorMessage =
            response.message == commonConstant.MESSAGE.unauthorized
              ? this.commonService.trans('message.unauthorized')
              : response.message;
          this.commonService.showToastyError(
            this.commonService.trans(errorMessage),
          );
          clearTimeout(timeout);
        });
      }
    }

    return response;
  }

  saveTokenLocal(accessToken: string, refreshToken: string, deviceId?: string) {
    document.cookie = `${this.keyAccessToken}=${accessToken}; path=/`;
    document.cookie = `${this.keyRefreshToken}=${refreshToken}; path=/`;
    localStorage.setItem('deviceId', deviceId || this.getDeviceId);
  }

  removeTokenLocal() {
    document.cookie = `${this.keyAccessToken}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    document.cookie = `${this.keyRefreshToken}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  get getAccessToken() {
    return this.getCookie(this.keyAccessToken);
  }

  get getRefreshToken() {
    return this.getCookie(this.keyRefreshToken);
  }

  private getCookie(name: string): string | null {
    const match = document.cookie.match(
      new RegExp('(^| )' + name + '=([^;]+)'),
    );
    return match ? decodeURIComponent(match[2]) : null;
  }

  get getDeviceId() {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      deviceId = uuidv4();
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }

  get getProfile() {
    return this.$accountLogin.asObservable();
  }

  get getUser() {
    return this.$accountLogin.getValue();
  }

  checkRole(roles: any) {
    return this.$accountLogin.pipe(
      map((user) => {
        const role = user?.role;
        if (role) {
          return roles.includes(role);
        }
        return false;
      }),
    );
  }
}
